import './RightClickContextMenu.scss';

import { useAurigaContext } from '@context/AurigaContext';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { MenuOption } from 'types/Common.types';
import { ContextMenuEnums } from 'types/Page.types';

/**
 * Right-click context menu properties
 */
interface RightClickContextMenuProps<TValueEnum> {
  options?: MenuOption<TValueEnum>[];
  onAction: (actionValue: TValueEnum) => void;
  children: ReactNode;
}

/**
 * Reusable right-click context menu component
 */
const RightClickContextMenu = <TValueEnum,>({
  options,
  onAction,
  children,
}: RightClickContextMenuProps<TValueEnum>) => {
  // To limit right click menu items based on enabled Feature Flags
  const { featureFlags } = useAurigaContext();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  /**
   * Handles right-click context menu events and updates internal options ref when options change
   * @param event - The mouse event from right-clicking
   */
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    // Prevent context menu on header elements
    const target = event.target as HTMLElement;
    if (target.closest('.ag-header, .ag-header-cell, .ag-header-group-cell')) {
      return;
    }

    event.preventDefault();

    // Always close the existing menu first, then open at new position
    setContextMenu(null);

    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  }, []);

  const handleClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Close menu on any click when menu is open
  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => setContextMenu(null);
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
    return undefined;
  }, [contextMenu]);

  /**
   * Handles menu item action selection
   * @param actionValue - The value of the selected menu option
   */
  const handleAction = useCallback(
    (actionValue: TValueEnum) => {
      onAction(actionValue);
      setContextMenu(null);
    },
    [onAction],
  );

  /**
   * Filters menu options based on feature flags, to hide or show options to the User
   */
  const enabledMenuOptions = options?.filter((option) => {
    // Handle feature flag-enabled menu options
    if (option.value === ContextMenuEnums.FOOTNOTES) {
      return featureFlags.enableFootnotes;
    }

    if (option.value === ContextMenuEnums.COMMENT) {
      return featureFlags.enableComments;
    }

    if (option.value === ContextMenuEnums.DECKLINE_HISTORY) {
      return featureFlags.enableAuditHistory;
    }

    if (option.value === ContextMenuEnums.DEBUG_OUTPUT_DECKLINE) {
      // Returns `true` for DEV env only
      return process.env.NODE_ENV === 'development';
    }

    // Enable all other non-featured flag menu options
    return true;
  });

  return (
    <Box>
      <Box onContextMenu={handleContextMenu}>{children}</Box>
      <Menu
        className='right-click-context-menu'
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {enabledMenuOptions &&
          enabledMenuOptions.map((option, index) => [
            <MenuItem
              key={option.value as string}
              onClick={() => handleAction(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>,
            option.divider && index < enabledMenuOptions.length - 1 && <Divider key={`divider-${option.value}`} />,
          ])}
      </Menu>
    </Box>
  );
};

export default RightClickContextMenu;
